import React, { Dispatch, SetStateAction, useContext, useEffect, useState } from "react"
import tw from "twin.macro"
import { Routes } from "../../../routes"
import { useTranslation } from "next-i18next"
import ReactGA from "react-ga"
import { Button } from "../../formElements/button"
import { useCreateUserMutation, useIsUsernameValidLazyQuery } from "../../../graphql/generated/graphql"
import { Input } from "../../formElements/input"
import { Events, registerEvent } from "../../../analytics"
import getUserLocale from "get-user-locale"
import { DateTime } from "luxon"
import { AuthContext } from "../../../utils/context"
import { useRouter } from "next/router"
import { handleRedirect } from "../../../utils/redirect"
import { UsernameInput } from "../../formElements/usernameInput"
import { PasswordInput } from "../../formElements/passwordInput"
import { Partner } from "../../partners/hero"
import { setLoginCookie } from "../../../utils/cookie"
import { useDebounce } from "../../../utils/hooks/useDebounce"

interface CreateAccountProps {
  email: string
  phoneNumber: string
  phoneCountryCode: number
  setShow?: Dispatch<SetStateAction<boolean>>
  setNotDismissable?: Dispatch<SetStateAction<boolean>>
  redirectUserAfterCreation?: boolean
}

enum CampaignId {
  Symphonic = "symphonic",
  Discmakers = "discmakers23",
}

export const CreateAccount = ({
  email,
  phoneNumber,
  phoneCountryCode,
  setShow,
  setNotDismissable,
  redirectUserAfterCreation,
}: CreateAccountProps) => {
  const { setUserId, setToken, setPreflightData, setPreflightMutationCalled, setPreflightDataLoaded, referredByUser } =
    useContext(AuthContext)
  const { t } = useTranslation("authentication")
  const [isProcessing, setProcessing] = useState(false)
  const [name, setName] = useState("")
  const [password, setPassword] = useState("")
  const [username, setUsername] = useState("")
  const [invalidUsername, setInvalidUsername] = useState<string | undefined>("")
  const validated = invalidUsername === undefined && password.trim().length >= 8 && name.length > 0
  const [checkUsernameAvailable, { loading: usernameAvailableLoading }] = useIsUsernameValidLazyQuery()
  const [createUser] = useCreateUserMutation()
  const privacyPolicyURL = `${Routes.PrivacyPolicy}`
  const termsOfUseURL = `${Routes.TermsOfUse}`
  const termsAndConditionsURL = `${Routes.TermsAndConditions}`
  const router = useRouter()
  const partner = router.pathname.replace("/", "")
  let campaignId: string

  switch (partner) {
    case Partner.Discmakers:
      campaignId = CampaignId.Discmakers
      break
    case Partner.Symphonic:
      campaignId = CampaignId.Symphonic
      break
    default:
      campaignId = `${router.query.campaignId}`
      break
  }

  useEffect(() => {
    setNotDismissable?.(true)
  }, [])

  const cleanInput = (str: string) => {
    return str.replace(/[^A-Z0-9_]+/gi, "").toLowerCase()
  }

  const validateUsernameAvailability = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const res = await checkUsernameAvailable({ variables: { username: cleanInput(e.target.value) } })
    if (!!res.data && res.data.UsernameValid.isValid === false) {
      setInvalidUsername(res.data.UsernameValid.message ?? "")
    } else {
      setInvalidUsername(undefined)
    }
  }
  const usernameValidation = useDebounce(validateUsernameAvailability, 1000)

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    if (name === "name") {
      setInvalidUsername("")
      setName(value)
      setUsername(cleanInput(value))
      usernameValidation(e)
    } else if (name === "username") {
      setInvalidUsername("")
      setUsername(cleanInput(value))
      usernameValidation(e)
    } else {
      setPassword(value)
    }
  }

  const submit = async (e: React.MouseEvent<Element, MouseEvent>) => {
    e.preventDefault()
    if (validated) {
      setProcessing(true)
      await save()
    }
  }
  const save = async () => {
    const response = await createUser({
      variables: {
        name,
        email,
        phoneNumber,
        phoneCountryCode,
        username,
        password,
        path: (router?.query?.redirect as string) ?? router.asPath,
        campaignCode: campaignId,
        timezone: DateTime.local().zoneName,
        referredByUser,
        locale: getUserLocale(),
      },
    })
    if (!!response?.data?.CreateUser?.success) {
      const { userId, token } = response.data.CreateUser

      // ping facebook
      try {
        if (process.env.NEXT_PUBLIC_ENV === "production") {
          ReactGA.event({
            category: "User",
            action: "fill data",
          })
          registerEvent(Events.SignedUp)
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          if (typeof fbq !== "undefined") {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            fbq("init", "144790910944085", {
              em: email.toLowerCase(),
              external_id: userId,
            })
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            fbq("track", "CompleteRegistration")
          }
        }
      } catch (e) {
        console.log("error ping facebook", e)
      }

      const doRedirect = redirectUserAfterCreation ?? true

      if (!!token) {
        setLoginCookie(token)
        setProcessing(false)
        setShow?.(false)
        setToken(token)
        !!userId && setUserId(userId)
        setPreflightData(undefined)
        setPreflightDataLoaded(false)
        setPreflightMutationCalled(false)
        doRedirect && (await handleRedirect(username, router)) //we need to use this util func instead of router.push to prevent redirecting in profile
      }
    }
  }

  return (
    <div tw="z-0 w-full h-full text-base-6 top-0 bottom-0 flex flex-col space-y-6">
      <h3 tw="title-3">{t("join.sign_up")}</h3>
      <form id="signupForm">
        <Input
          label={t("join.input_name_label")}
          placeholder={t("join.input_name_label")}
          name="name"
          inputValue={name}
          handleChange={handleChange}
          tabIndex={1}
          required={true}
        />
        <UsernameInput
          label={t("join.input_username_label")}
          name="username"
          inputValue={username}
          handleChange={handleChange}
          loading={usernameAvailableLoading}
          placeholder={username}
          tabIndex={2}
          invalid={!!invalidUsername}
          helpText={invalidUsername}
          required={true}
        />
        <PasswordInput
          label={t("join.input_password_label")}
          placeholder={t("join.input_password_label")}
          name="password"
          autoComplete="newPassword" // value has dashes will not work in chrome
          inputValue={password}
          handleChange={handleChange}
          helpText={t("join.input_password_helptext")}
          tabIndex={3}
          required={true}
        />
        <p
          tw="text-xs pt-2 pb-5"
          dangerouslySetInnerHTML={{
            __html: t("join.checkbox_terms", { privacyPolicyURL, termsOfUseURL, termsAndConditionsURL }),
          }}
        />
        <Button
          dataTestId="signup-button"
          action={t("join.sign_up")}
          twStyle={tw`w-full`}
          onClickAsync={submit}
          isProcessing={isProcessing}
          isDisabled={!validated}
        />
      </form>
    </div>
  )
}
